import { useCallback, useEffect, useMemo } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  AnalyticsFilterInfo,
  analyticsFilterInfoAtom,
  areAnalyticsFilterDatesStale,
  getDefaultAnalyticsFilterInfo,
  hasAnalyticsPresetDateRange,
  resolveAnalyticsFilterInfo,
  isAnalyticsFilterAppliedAtom,
  isAnalyticsHydratedAtom,
  resetAnalyticsFilterAtom,
  setAnalyticsFilterAtom,
  setIsAnalyticsFilterAppliedAtom,
  setIsAnalyticsHydratedAtom,
} from "@/store/analytics";
import {
  GetAnalyticsFilterStateUseCase,
  ResetAnalyticsFilterStateUseCase,
  SaveAnalyticsFilterStateUseCase,
} from "@/core/usecases/analytics";
import { AnalyticsFilterStateRepository } from "@/infrastructure/repositories/analyticsFilterState";

interface UseAnalyticsFilter {
  filterInfo: AnalyticsFilterInfo;
  isFilterApplied: boolean;
  isHydrated: boolean;
  setFilter: (next: AnalyticsFilterInfo) => Promise<void>;
  clearFilter: () => Promise<void>;
  setIsFilterApplied: (isFilterApplied: boolean) => void;
  /** Recomputes rolling preset dates (e.g. Last 1 month) from the current system date. */
  refreshPresetDates: () => Promise<boolean>;
}

export const useAnalyticsFilter = (): UseAnalyticsFilter => {
  const repository = useMemo(() => new AnalyticsFilterStateRepository(), []);
  const getStateUseCase = useMemo(() => new GetAnalyticsFilterStateUseCase(repository), [repository]);
  const saveStateUseCase = useMemo(() => new SaveAnalyticsFilterStateUseCase(repository), [repository]);
  const resetStateUseCase = useMemo(() => new ResetAnalyticsFilterStateUseCase(repository), [repository]);

  const filterInfo = useAtomValue(analyticsFilterInfoAtom);
  const isFilterApplied = useAtomValue(isAnalyticsFilterAppliedAtom);
  const isHydrated = useAtomValue(isAnalyticsHydratedAtom);

  const writeFilter = useSetAtom(setAnalyticsFilterAtom);
  const writeIsFilterApplied = useSetAtom(setIsAnalyticsFilterAppliedAtom);
  const writeIsHydrated = useSetAtom(setIsAnalyticsHydratedAtom);
  const resetFilter = useSetAtom(resetAnalyticsFilterAtom);

  useEffect(() => {
    if (isHydrated) return;
    const hydrate = async () => {
      const saved = await getStateUseCase.execute();
      const loaded = (saved as AnalyticsFilterInfo | null) ?? getDefaultAnalyticsFilterInfo();
      writeFilter(resolveAnalyticsFilterInfo(loaded));
      writeIsFilterApplied(!!saved);
      writeIsHydrated(true);
    };
    hydrate();
  }, [isHydrated, getStateUseCase, writeFilter, writeIsFilterApplied, writeIsHydrated]);

  const setFilter = useCallback(
    async (next: AnalyticsFilterInfo) => {
      const resolved = resolveAnalyticsFilterInfo(next);
      writeFilter(resolved);
      writeIsFilterApplied(true);
      await saveStateUseCase.execute(resolved);
    },
    [writeFilter, writeIsFilterApplied, saveStateUseCase]
  );

  const refreshPresetDates = useCallback(async (): Promise<boolean> => {
    if (!hasAnalyticsPresetDateRange(filterInfo)) {
      return false;
    }

    const resolved = resolveAnalyticsFilterInfo(filterInfo);
    if (!areAnalyticsFilterDatesStale(filterInfo, resolved)) {
      return false;
    }

    writeFilter(resolved);
    if (isFilterApplied) {
      await saveStateUseCase.execute(resolved);
    }

    return true;
  }, [filterInfo, isFilterApplied, writeFilter, saveStateUseCase]);

  const clearFilter = useCallback(async () => {
    resetFilter(getDefaultAnalyticsFilterInfo());
    await resetStateUseCase.execute();
  }, [resetFilter, resetStateUseCase]);

  return {
    filterInfo,
    isFilterApplied,
    isHydrated,
    setFilter,
    clearFilter,
    setIsFilterApplied: writeIsFilterApplied,
    refreshPresetDates,
  };
};
